import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, Loader2, Image as ImageIcon } from "lucide-react";
import { companyInfo } from "../data";
import ImageModal from "../components/ImageModal";
import useMetadata from "../hooks/useMetadata";

const benefitsMap = {
  udgravning: [
    "Millimeterpræcision med moderne 3D-GPS maskinstyring",
    "Erfarne maskinførere med fokus på sikkerhed og kvalitet",
    "Udgravning tæt på eksisterende bygninger uden risiko",
    "Komplet håndtering og bortskaffelse af opgravet jord",
  ],
  fundamenter: [
    "Korrekt dybde i overensstemmelse med frostfri dybde (min. 90 cm)",
    "Millimeterpræcis nivellering med avanceret laserudstyr",
    "Klargjort fuldstændigt, så mureren kan støbe med det samme",
    "Erfaring med både stribefundamenter og punktfundamenter",
  ],
  jordhaandtering: [
    "Hurtig og effektiv flytning af store jordmængder",
    "Lovlig, dokumenteret og miljøgodkendt bortskaffelse",
    "Professionel terrænregulering og planering af byggegrunde",
    "Etablering af støjvolde og terrænregulering i haveanlæg",
  ],
  sandpuder: [
    "Anvendelse af certificeret, stabilt og rent fyldsand",
    "Gradvis opbygning og grundig komprimering i korrekte lagtykkelser",
    "Løbende komprimeringskontrol og afsluttende belastningstest",
    "Garanteret stabilt og sætningsfrit underlag for dit byggeri",
  ],
};

export default function ServiceDetail() {
  const { serviceUrl } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useMetadata(
    service ? service.titel : "Ydelse",
    service ? service.kort_beskrivelse : "Læs mere om vores professionelle ydelser inden for jord-, grave- og anlægsarbejde."
  );

  useEffect(() => {
    async function fetchServiceData() {
      setLoading(true);
      try {
        // 1. Fetch service by URL field
        const serviceRes = await fetch(
          `https://api.dev.auxo.dk/public/v2/content/ydelser?url=${serviceUrl}`
        );
        const serviceData = await serviceRes.json();

        if (!serviceData.items || serviceData.items.length === 0) {
          // If service not found, redirect to 404
          navigate("/404", { replace: true });
          return;
        }

        const currentService = serviceData.items[0];
        setService(currentService);

        // 2. Fetch associated gallery using galleri_reference
        const galleryRes = await fetch(
          `https://api.dev.auxo.dk/public/v2/content/galleri?galleri_slug=${currentService.galleri_reference}`
        );
        const galleryData = await galleryRes.json();
        if (galleryData.items && galleryData.items.length > 0) {
          setGallery(galleryData.items[0]);
        }

        // 3. Fetch projects and filter by service title
        const projectsRes = await fetch(
          "https://api.dev.auxo.dk/public/v2/content/projekter"
        );
        const projectsData = await projectsRes.json();
        if (projectsData.items) {
          const filtered = projectsData.items.filter((project) =>
            project.udfoerte_ydelser.da
              .toLowerCase()
              .includes(currentService.titel.da.toLowerCase())
          );
          setRelatedProjects(filtered);
        }
      } catch (error) {
        console.error("Fejl ved hentning af ydelsesdetaljer:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchServiceData();
  }, [serviceUrl, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-dark text-white">
        <div className="text-center space-y-3">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand-accent" />
          <p className="text-sm text-gray-400">Henter oplysninger om ydelsen...</p>
        </div>
      </div>
    );
  }

  if (!service) return null;

  const benefits = benefitsMap[serviceUrl] || [];

  return (
    <div className="relative bg-brand-dark text-white">
      {/* Hero Section */}
      <section className="relative flex min-h-[45vh] items-center justify-center overflow-hidden py-16">
        <div className="absolute inset-0 z-0">
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/${service.hero_billede[0].replace(
              "https://assets.auxodev.dk/",
              ""
            )}`}
            alt={service.titel.da}
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/80 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block rounded bg-brand-accent/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-accent mb-4">
            Specialiseret ydelse
          </span>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl uppercase">
            {service.titel.da}
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base md:text-lg text-gray-300">
            {service.kort_beskrivelse.da}
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 sm:py-24 border-t border-brand-gray-dark bg-brand-charcoal/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Left Column: Description */}
            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl uppercase">
                Professionel udførelse af {service.titel.da.toLowerCase()}
              </h2>
              <div
                className="prose prose-invert max-w-none text-gray-300 text-sm sm:text-base leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: service.hovedindhold.da }}
              />
            </div>

            {/* Right Column: Benefits */}
            <div className="lg:col-span-5">
              <div className="rounded-lg border border-brand-gray-dark bg-brand-charcoal/60 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-white mb-6 uppercase">
                  Dine fordele hos Godske
                </h3>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-brand-accent shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-300 leading-relaxed">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {gallery && gallery.billeder && gallery.billeder.length > 0 && (
        <section className="py-16 sm:py-24 border-t border-brand-gray-dark bg-brand-dark">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent">
                Billedgalleri
              </span>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl mt-2 uppercase">
                Se vores arbejde med {service.titel.da.toLowerCase()}
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {gallery.billeder.map((imgUrl, index) => (
                <div
                  key={index}
                  onClick={() => setActiveImageIndex(index)}
                  className="group relative h-64 overflow-hidden rounded-lg border border-brand-gray-dark bg-brand-charcoal cursor-pointer"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,height=380,fit=cover,quality=80,format=auto/${imgUrl.replace(
                      "https://assets.auxodev.dk/",
                      ""
                    )}`}
                    alt={`${service.titel.da} galleri ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Projects Section */}
      {relatedProjects.length > 0 && (
        <section className="py-16 sm:py-24 border-t border-brand-gray-dark bg-brand-charcoal/10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent">
                Udførte opgaver
              </span>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl mt-2 uppercase">
                Relevante projekter
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {relatedProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projekter/${project.projekt_slug}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-brand-gray-dark bg-brand-dark/40 transition-all hover:border-brand-accent/30"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,height=350,fit=cover,quality=80,format=auto/${project.galleri[0].replace(
                        "https://assets.auxodev.dk/",
                        ""
                      )}`}
                      alt={project.projekttitel.da}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 rounded bg-brand-dark/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-accent">
                      {project.lokation.da}
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="text-base font-bold text-white group-hover:text-brand-accent transition-colors line-clamp-1 uppercase">
                      {project.projekttitel.da}
                    </h3>
                    <p className="mt-2 text-[11px] text-brand-gray uppercase tracking-wider font-semibold">
                      Ydelser: {project.udfoerte_ydelser.da}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="border-t border-brand-gray-dark bg-brand-charcoal/40 py-20 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-white sm:text-3xl uppercase">
            Skal vi hjælpe dig med {service.titel.da.toLowerCase()}?
          </h2>
          <p className="mt-3 text-gray-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Kontakt os i dag for en uforpligtende snak om dit projekt. Vi rådgiver dig gerne om den bedste løsning og udarbejder et uforpligtende tilbud.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded bg-brand-accent px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-accent-hover"
            >
              Få et tilbud
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded border border-brand-gray-dark bg-brand-dark/50 px-6 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-gray-dark"
            >
              <Phone className="h-4 w-4 text-brand-accent" />
              <span>Ring {companyInfo.phoneFormatted}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && gallery && (
        <ImageModal
          images={gallery.billeder}
          initialIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
        />
      )}
    </div>
  );
}
